<template>
  <v-dialog
    :model-value="showConfirmDialog"
    :max-width="props.maxWidth"
    persistent
  >
    <v-card>
      <!-- Header -->
      <v-card-title class="d-flex align-center">
        <v-icon :color="typeColor" :icon="typeIcon" class="mr-2"></v-icon>
        <span>{{ props.title }}</span>
        <v-spacer></v-spacer>
        <v-btn icon size="small" @click="cancel">
          <v-icon>mdi-close</v-icon>
          <v-tooltip activator="parent" location="bottom">Close</v-tooltip>
        </v-btn>
      </v-card-title>

      <!-- Message -->
      <v-card-text class="pt-4">
        <div v-if="props.message" class="text-body-1 mb-3">
          {{ props.message }}
        </div>

        <div v-if="props.htmlMessage" v-html="props.htmlMessage" class="text-body-1"></div>

        <!-- Input for confirmation text -->
        <v-text-field
          v-if="props.requireText"
          v-model="inputText"
          :label="props.textLabel"
          :placeholder="props.textPlaceholder"
          density="comfortable"
          variant="outlined"
          class="mt-4"
          @keyup.enter="confirmAction"
          :error="inputError"
          :error-messages="inputError ? 'Confirmation text does not match' : ''"
        ></v-text-field>

        <!-- Warning/Info/Danger text -->
        <div
          v-if="props.warningText"
          class="d-flex align-start mt-3 pa-3 rounded"
          :class="warningClass"
        >
          <v-icon class="mr-2 mt-1" size="small">{{ warningIcon }}</v-icon>
          <span class="text-body-2">{{ props.warningText }}</span>
        </div>
      </v-card-text>

      <!-- Actions -->
      <v-card-actions class="pa-4 pt-0">
        <v-spacer></v-spacer>

        <v-btn variant="outlined" @click="cancel">
          {{ props.cancelText }}
        </v-btn>

        <!-- Optional third action button (e.g. "Save" in unsaved-changes dialogs) -->
        <v-btn
          v-if="props.thirdText"
          :color="props.thirdColor"
          variant="flat"
          @click="thirdAction"
        >
          {{ props.thirdText }}
        </v-btn>

        <v-btn
          :color="confirmColor"
          variant="flat"
          @click="confirmAction"
          :disabled="isConfirmDisabled"
        >
          {{ props.confirmText }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useConfirmDialog } from "@/composables/useConfirmDialog.js";

const { showConfirmDialog, confirmDialogProps, handleConfirm, handleCancel, handleThird } = useConfirmDialog();

const props = computed(() => confirmDialogProps.value);

const inputText = ref("");
const inputError = ref(false);

const typeColor = computed(() => {
  const colors = { info: "info", warning: "warning", danger: "error", success: "success" };
  return colors[props.value.type] || "warning";
});

const typeIcon = computed(() => {
  const icons = {
    info: "mdi-information",
    warning: "mdi-alert-circle",
    danger: "mdi-alert-octagon",
    success: "mdi-check-circle",
  };
  return icons[props.value.type] || "mdi-alert-circle";
});

const confirmColor = computed(() => {
  const colors = { info: "primary", warning: "warning", danger: "error", success: "success" };
  return colors[props.value.type] || "warning";
});

const warningClass = computed(() => {
  const classes = {
    info: "bg-blue-lighten-5 text-blue-darken-3",
    warning: "bg-orange-lighten-5 text-orange-darken-3",
    danger: "bg-red-lighten-5 text-red-darken-3",
    success: "bg-green-lighten-5 text-green-darken-3",
  };
  return classes[props.value.type] || "bg-orange-lighten-5 text-orange-darken-3";
});

const warningIcon = computed(() => {
  const icons = {
    info: "mdi-information-outline",
    warning: "mdi-alert-outline",
    danger: "mdi-alert-circle-outline",
    success: "mdi-check-circle-outline",
  };
  return icons[props.value.type] || "mdi-alert-outline";
});

const isConfirmDisabled = computed(() => {
  if (props.value.requireText) {
    return inputText.value !== props.value.requiredText;
  }
  return false;
});

const confirmAction = () => {
  if (props.value.requireText && inputText.value !== props.value.requiredText) {
    inputError.value = true;
    return;
  }
  reset();
  handleConfirm();
};

const cancel = () => {
  reset();
  handleCancel();
};

/** Handle third action button click */
const thirdAction = () => {
  reset();
  handleThird();
};

const reset = () => {
  inputText.value = "";
  inputError.value = false;
};

watch(showConfirmDialog, (newVal) => {
  if (!newVal) {
    reset();
  }
});
</script>

<style scoped>
.v-dialog {
  border-radius: 8px;
}

.v-card {
  border-radius: 8px;
}
</style>
